import { useCallback, useEffect, useRef, useState } from "react";
import type { WorldTerm } from "../lib/world";
import { termSoundDurationForTick } from "../lib/timing";

const STORAGE_KEY = "recaman:sound:v1";

function readInitialSoundPreference(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "on";
  } catch {
    return false;
  }
}

export function useTermSound(term: WorldTerm | undefined, tickMs: number) {
  const [enabled, setEnabled] = useState(readInitialSoundPreference);
  const contextRef = useRef<AudioContext | null>(null);
  const lastTermRef = useRef<string | null>(null);

  const toggle = useCallback(async () => {
    const next = !enabled;
    if (next) {
      contextRef.current ??= new AudioContext();
      await contextRef.current.resume();
    }
    setEnabled(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next ? "on" : "off");
    } catch {
      // Storage is optional; sound still works for the current tab.
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !term || document.visibilityState !== "visible") return;
    if (lastTermRef.current === term.n) return;
    lastTermRef.current = term.n;
    const context = contextRef.current;
    if (!context) return;

    const value = BigInt(term.value);
    const pitchClass = Number(value % 12n);
    const frequency = 174.61 * 2 ** (pitchClass / 12);
    const duration = termSoundDurationForTick(tickMs) / 1_000;
    const attackEnd = Math.min(0.012, duration * 0.2);
    const releaseEnd = Math.max(attackEnd + 0.005, duration - 0.02);
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = term.operation === "add" ? "sine" : "triangle";
    oscillator.frequency.setValueAtTime(frequency, context.currentTime);
    gain.gain.setValueAtTime(0, context.currentTime);
    gain.gain.linearRampToValueAtTime(0.028, context.currentTime + attackEnd);
    gain.gain.exponentialRampToValueAtTime(
      0.0001,
      context.currentTime + releaseEnd,
    );
    oscillator.connect(gain).connect(context.destination);
    oscillator.start();
    oscillator.stop(context.currentTime + duration);
  }, [enabled, term, tickMs]);

  useEffect(() => () => void contextRef.current?.close(), []);

  return { enabled, toggle };
}
